import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchSession, login as loginRequest, register as registerRequest } from '../api/resources';
import type { AppContext, AuthResponse, RegistrationPayload, SessionData, User } from '../types';
import { clearAuthStorage, getAccessToken, getStoredContextId, setAccessToken, setStoredContextId } from './storage';

interface AuthValue {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: User | null;
  contexts: AppContext[];
  activeContext: AppContext | null;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegistrationPayload) => Promise<void>;
  logout: () => void;
  selectContext: (contextId: string) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Stellt den zuletzt verwendeten Kontext wieder her und fällt sonst auf den ersten erlaubten Kontext zurück. */
function selectInitialContext(contexts: AppContext[]): AppContext | null {
  const storedId = getStoredContextId();
  return contexts.find((context) => context.id === storedId) ?? contexts[0] ?? null;
}

/** Verwaltet JWT, Benutzer und den aktiven serverseitig erlaubten Arbeitskontext. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthValue['status']>(() => getAccessToken() ? 'loading' : 'anonymous');
  const [user, setUser] = useState<User | null>(null);
  const [contexts, setContexts] = useState<AppContext[]>([]);
  const [activeContext, setActiveContext] = useState<AppContext | null>(null);

  function applySession(session: SessionData): void {
    const context = selectInitialContext(session.contexts);
    setUser(session.user);
    setContexts(session.contexts);
    setActiveContext(context);
    if (context) setStoredContextId(context.id);
    setStatus('authenticated');
  }

  function applyAuthResponse(response: AuthResponse): void {
    setAccessToken(response.accessToken);
    applySession({ user: response.user, contexts: response.contexts });
  }

  function logout(): void {
    clearAuthStorage();
    setUser(null);
    setContexts([]);
    setActiveContext(null);
    setStatus('anonymous');
  }

  useEffect(() => {
    // Ein vorhandenes JWT wird beim App-Start über /auth/me erneut serverseitig geprüft.
    if (!getAccessToken()) return;
    void fetchSession().then(applySession).catch(logout);
  }, []);

  const value = useMemo<AuthValue>(() => ({
    status,
    user,
    contexts,
    activeContext,
    login: async (email, password) => {
      applyAuthResponse(await loginRequest(email, password));
    },
    register: async (payload) => {
      applyAuthResponse(await registerRequest(payload));
    },
    logout,
    selectContext: (contextId) => {
      const context = contexts.find((entry) => entry.id === contextId);
      if (!context) return;
      setActiveContext(context);
      setStoredContextId(context.id);
    }
  }), [activeContext, contexts, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Liefert den Authentifizierungszustand an geschützte Seiten und API-nahe Komponenten. */
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth muss innerhalb des AuthProvider verwendet werden.');
  return value;
}
